/**
 * CopilotChat — module-scoped AI conversation UI.
 *
 * Wraps `sendCopilotChat(copilot, …)`. Each conversation stays on the client
 * (no persistence yet); the transcript is sent with every request so the
 * model has full context. Trace entries returned by the backend are rendered
 * under each assistant message so the user can see which tools the model
 * invoked — useful for trust ("did it check the real data?") and for debug.
 *
 * Layout:
 *   [header]         — copilot label + intro
 *   [suggestions]    — click-to-send starter prompts when empty
 *   [message list]   — alternating user/assistant bubbles
 *                      assistant bubble includes an expandable tool trace
 *   [composer]       — textarea + send
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Bot, ChevronDown, ChevronRight, Send, User2, Wrench } from "lucide-react";

import {
  COPILOT_META,
  sendCopilotChat,
  type ChatMessage,
  type ToolTraceEntry,
} from "@/api/ai";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useToast } from "@/hooks/useToast";

interface DisplayMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  pending?: boolean;
  trace?: ToolTraceEntry[];
  iterations?: number;
}

interface CopilotChatProps {
  copilot: string;
  className?: string;
  compact?: boolean;
}

export function CopilotChat({ copilot, className, compact = false }: CopilotChatProps) {
  const { addToast } = useToast();
  const meta = COPILOT_META[copilot] || COPILOT_META.general;

  const [messages, setMessages] = useState<DisplayMessage[]>([]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Reset conversation when the caller switches copilots.
  useEffect(() => {
    setMessages([]);
    setInput("");
  }, [copilot]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages]);

  const isFresh = messages.length === 0;

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || isSending) return;

    const userMsg: DisplayMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: trimmed,
    };
    const pending: DisplayMessage = {
      id: crypto.randomUUID(),
      role: "assistant",
      content: "Thinking…",
      pending: true,
    };
    setMessages((prev) => [...prev, userMsg, pending]);
    setInput("");
    setIsSending(true);

    try {
      const history: ChatMessage[] = [...messages, userMsg]
        .filter((m) => !m.pending)
        .map((m) => ({ role: m.role, content: m.content }));

      const result = await sendCopilotChat(copilot, history);
      setMessages((prev) =>
        prev.map((m) =>
          m.id === pending.id
            ? {
                ...m,
                content: result.reply || "(no response)",
                pending: false,
                trace: result.trace,
                iterations: result.iterations,
              }
            : m,
        ),
      );
    } catch {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === pending.id
            ? {
                ...m,
                content:
                  "I ran into an error talking to the AI service. Please try again.",
                pending: false,
              }
            : m,
        ),
      );
      addToast("error", "AI request failed");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  const heightClass = compact ? "h-[28rem]" : "h-[calc(100vh-14rem)]";

  return (
    <Card
      className={`flex flex-col overflow-hidden ${heightClass} ${className || ""}`}
      padding={false}
    >
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
        {isFresh ? (
          <FreshState meta={meta} onPick={send} />
        ) : (
          messages.map((m) => (
            <MessageBubble key={m.id} message={m} />
          ))
        )}
      </div>

      <div className="border-t border-border p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={1}
            placeholder={`Ask the ${meta.label.toLowerCase()} copilot…`}
            className="flex-1 resize-none rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent max-h-32"
          />
          <Button
            onClick={() => send(input)}
            disabled={!input.trim() || isSending}
            isLoading={isSending}
          >
            <Send className="h-4 w-4" />
            Send
          </Button>
        </div>
      </div>
    </Card>
  );
}


function FreshState({
  meta,
  onPick,
}: {
  meta: { label: string; intro: string; suggestions: string[] };
  onPick: (s: string) => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center h-full text-center">
      <div className="rounded-full bg-accent/10 p-3 mb-3">
        <Bot className="h-6 w-6 text-accent" />
      </div>
      <h3 className="text-base font-semibold font-heading text-text-primary mb-1">
        {meta.label} copilot
      </h3>
      <p className="text-sm text-text-muted max-w-md mb-5">{meta.intro}</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-xl">
        {meta.suggestions.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="text-left rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary hover:border-accent hover:bg-accent/5 transition-colors"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}


function MessageBubble({ message }: { message: DisplayMessage }) {
  const isUser = message.role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center">
          <Bot className="h-4 w-4 text-accent" />
        </div>
      )}
      <div className="max-w-[75%] space-y-2">
        <div
          className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
            isUser
              ? "bg-accent text-white"
              : "bg-background text-text-primary"
          } ${message.pending ? "animate-pulse" : ""}`}
        >
          {message.content}
        </div>
        {!isUser && message.trace && message.trace.length > 0 && (
          <ToolTrace trace={message.trace} iterations={message.iterations} />
        )}
      </div>
      {isUser && (
        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-background flex items-center justify-center">
          <User2 className="h-4 w-4 text-text-muted" />
        </div>
      )}
    </div>
  );
}


function ToolTrace({
  trace,
  iterations,
}: {
  trace: ToolTraceEntry[];
  iterations?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  const label = useMemo(() => {
    const names = trace.map((t) => t.tool);
    const unique = Array.from(new Set(names));
    return unique.join(", ");
  }, [trace]);

  return (
    <div className="rounded-lg border border-border bg-surface text-[11px]">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-text-muted hover:text-text-primary transition-colors"
      >
        {expanded ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <Wrench className="h-3 w-3" />
        <span className="font-medium">
          {trace.length} tool call{trace.length === 1 ? "" : "s"}
          {iterations ? ` · ${iterations} turn${iterations === 1 ? "" : "s"}` : ""}
        </span>
        <span className="text-text-muted truncate">— {label}</span>
      </button>
      {expanded && (
        <div className="px-3 pb-2 pt-1 space-y-2 font-mono">
          {trace.map((t, i) => (
            <div key={i} className="rounded bg-background p-2">
              <div className="flex items-center gap-1 text-accent mb-1">
                <Wrench className="h-3 w-3" />
                <span className="font-semibold">{t.tool}</span>
              </div>
              <div className="text-text-muted">
                <span className="text-text-primary">input:</span>{" "}
                {JSON.stringify(t.input)}
              </div>
              <div className="text-text-muted">
                <span className="text-text-primary">output:</span>{" "}
                <span className="break-all">
                  {JSON.stringify(t.output).slice(0, 400)}
                  {JSON.stringify(t.output).length > 400 ? "…" : ""}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
